/* =====================================================================
   campaignTargeting.js — where a campaign is allowed to apply
   ---------------------------------------------------------------------
   A campaign carries two optional targeting lists, both authored in the
   dashboard/nexio Καμπάνιες screen:

     surfaces : which SCREEN may offer it — 'customerClients' | 'phoneDesk' | 'till'
     channels : which ORDER KIND it applies to — the OrderKind values plus 'aggregator'

   BOTH ABSENT OR EMPTY MEAN EVERYWHERE. That is load-bearing, not a
   convenience: every campaign authored before these fields existed carries
   neither, and the admin's create form seeds them as []. Treating empty as
   "nowhere" would silently kill every campaign on every tenant. A list only
   ever RESTRICTS; it never grants.

   The customer clients cannot always name their own order kind yet — in the
   cart the customer has not chosen delivery vs takeaway — so the check takes
   the SET of kinds this order could still become and passes if any of them is
   allowed. The checkout re-checks with the single resolved kind, which is the
   second stage of the same gate.
   ===================================================================== */

export const SURFACE_CUSTOMER_CLIENTS = 'customerClients';

const cleanList = (value) => (Array.isArray(value) ? value.filter((v) => v) : []);

export const campaignAllowedOnSurface = (campaign, surface) => {
  const surfaces = cleanList(campaign?.surfaces);
  return surfaces.length === 0 || surfaces.includes(surface);
};

// `possibleKinds` is every OrderKind this order could still be committed as.
export const campaignAllowedOnKinds = (campaign, possibleKinds) => {
  const channels = cleanList(campaign?.channels);
  if (channels.length === 0) return true;
  return cleanList(possibleKinds).some((kind) => channels.includes(kind));
};

export const campaignTargetsCustomerClient = (campaign, possibleKinds) =>
  campaignAllowedOnSurface(campaign, SURFACE_CUSTOMER_CLIENTS) &&
  campaignAllowedOnKinds(campaign, possibleKinds);

/* The per-user coupon embeds a FROZEN copy of the campaign, taken when an admin
   assigned it. The server hook meant to refresh those copies is broken, so the
   frozen copy never changes — a campaign narrowed to app-only would keep
   applying for everyone already holding it. Join the live campaign by id.

   `campaignsById` null/undefined means the live list has not arrived; fall back
   to the frozen copy so a slow or failed fetch degrades to the old behaviour
   instead of dropping every coupon. A campaign MISSING from the live list also falls
   back to the frozen copy: the server delete cascade is broken, so holders of a campaign
   deleted months ago were never tombstoned, and treating absent as deleted would revoke
   every one of them in a single deploy with nothing shown to anyone. Revocation belongs to
   the tombstone (coupon.deleted / userActive) once that cascade is repaired. */
export const resolveCampaign = (coupon, campaignsById) => {
  const frozen = coupon?.userCoupon;
  if (!campaignsById || !frozen?._id) return frozen;
  return campaignsById.get(String(frozen._id)) || frozen;
};

export const campaignsToMap = (campaigns) =>
  new Map((campaigns || []).filter((c) => c?._id).map((c) => [String(c._id), c]));

/* What an applied VOUCHER is worth against the basket as it stands NOW.
   The cart used to price a typed code once, at the moment it was applied, and every later screen
   reused that frozen euro figure - and the campaign's minimumOrder was only checked at that same
   moment. So a "15% on orders from 25 euros" code applied to a 26 euro basket kept its 3.90 after
   the basket was emptied down to 5 euros (78% off), and anything added AFTER the code earned
   nothing. Re-derive it from the row's own type / reward / minimumOrder wherever money is computed.
   `base` is the PRODUCTS subtotal - the figure the cart priced it against - never transport.
   Rows written before minimumOrder was carried on them read as no minimum, which is exactly how
   they behaved when they were applied. */
export const voucherValueFor = (row, base) => {
  const reward = +row?.reward;
  const b = +base || 0;
  if (!(reward > 0) || !(b > 0)) return 0;
  if (b < (+row?.minimumOrder || 0)) return 0;
  const value = row?.type === 'Percent' ? b * reward / 100
    : row?.type === 'Cash' ? reward
    : 0;
  return +value.toFixed(2);
};
/* RECURRING money loyalty. A loyalty campaign (completion > 0) with `repeatable: true` pays a fresh
   reward every time the customer's spend since startDate passes another `completion` - "every 25
   euros spent earns 3.75". Without the flag a money loyalty campaign pays once per customer, ever
   (its code is the dedup key), which is how every campaign authored before this behaves and still
   does. Bonus campaigns already repeat per cycle of pieces and are not touched here.
   Each cycle is its own reward, so accrual can run after every order and still pay each cycle exactly
   once. Cycle 1 is keyed with the PLAIN campaign code and cycles 2.. as `${code}-cycle-${n}`: the plain
   code is what a once-only campaign and every older app build use as the dedup key, so an old app (or a
   campaign switched from once-only to repeating) sees cycle 1 as already held and never pays it twice.
   The server's till accrual (feathers-server services/loyalty/loyaltyRules.js) mints the same codes, and
   the users service merges by code, so the two never double up. The value per cycle is fixed:
   completion x reward / 100 for Percent, reward for Cash.
   `heldCodes` is every reward code the customer already holds, spent or not. */
export const repeatingLoyaltyRewards = (campaign, spend, heldCodes) => {
  const completion = +campaign?.completion;
  if (!campaign?.repeatable || !(completion > 0) || campaign?.type === 'Bonus') return [];
  const value = campaign.type === 'Percent' ? +(completion * +campaign.reward / 100).toFixed(2)
    : campaign.type === 'Cash' ? +(+campaign.reward).toFixed(2)
    : 0;
  if (!(value > 0)) return [];
  const held = new Set(heldCodes || []);
  //Spend is a sum of 2dp totals, so it can land a hair under a boundary (74.99999999 for 75):
  //round to cents first, and nudge the division, or a cycle is paid one order late.
  const cents = Math.round((+spend || 0) * 100) / 100;
  const cycles = Math.floor(cents / completion + 1e-9);
  const out = [];
  for (let n = 1; n <= cycles; n++) {
    const code = n === 1 ? campaign.code : `${campaign.code}-cycle-${n}`;
    if (!held.has(code)) out.push({
      code, campaignCode: campaign.code, type: campaign.type, value, applied: false,
      promotionText: campaign.promotionText, display: campaign.display
    });
  }
  return out;
};

/* Where a campaign's progress bar should sit. For a repeating campaign that is spend towards the
   NEXT reward - cumulative spend would pin the bar full forever after the first one. Every other
   campaign is unchanged. */
export const loyaltyProgress = (campaign, spend) => {
  const completion = +campaign?.completion;
  const s = Math.round((+spend || 0) * 100) / 100;
  if (!campaign?.repeatable || !(completion > 0) || campaign?.type === 'Bonus') return s;
  return +(s - completion * Math.floor(s / completion + 1e-9)).toFixed(2);
};
/* Has this customer already been paid a campaign's ONCE-ONLY reward? Either under its plain code, or
   as any cycle from a period when the campaign was repeating. Checking the plain code alone meant
   that unticking Repeatable on a live campaign paid one more reward to everyone who had already
   collected cycles - and put its finished progress bar back on their home screen. */
export const loyaltyPaidOnce = (campaign, heldCodes) => {
  const code = campaign?.code;
  if (!code) return false;
  const cyclePrefix = `${code}-cycle-`;
  return (heldCodes || []).some((c) => c === code || (typeof c === 'string' && c.startsWith(cyclePrefix)));
};

/* Spend `used` euros of the customer's pending money rewards, oldest first, and return the list.
   A reward fully used is marked applied; one only partly used keeps the remainder as its value and
   stays pending. Every unspent money reward used to be marked applied after ANY order, whatever the
   bill actually absorbed - so pending rewards larger than the bill (routine once a campaign repeats:
   one big order crossing two targets, then a small one) were simply thrown away.
   Bonus rewards are left alone: they are burned by their own delivered gift lines.
   `used` undefined keeps the old behaviour - spend everything - so a caller that does not say what
   it used can never hand out free money. */
export const consumeLoyaltyRewards = (rewards, used) => {
  const list = rewards || [];
  const pending = list.filter((r) => r && !r.bonus && !r.applied);
  if (used === undefined || used === null) {
    pending.forEach((r) => { r.applied = true; });
    return list;
  }
  let left = Math.round((+used || 0) * 100) / 100;
  for (const r of pending) {
    if (!(left > 0)) break;
    const value = Math.round((+r.value || 0) * 100) / 100;
    if (left >= value) { r.applied = true; left = Math.round((left - value) * 100) / 100; }
    else { r.value = Math.round((value - left) * 100) / 100; left = 0; }
  }
  return list;
};
